import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, Sparkles } from "lucide-react";
import { useRef } from "react";

const heroImage = "https://images.unsplash.com/photo-1779912217795-158d9efe51ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxoaWdoJTIwZmFzaGlvbiUyMGVkaXRvcmlhbCUyMGRhcmt8ZW58MHx8fHwxNzg3NjY3MjgwfDA&ixlib=rb-4.1.0&q=85";
const techImage = "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=900&q=85";

export function KineticHero({ onShop }) {
  const ref = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [7, -7]), { stiffness: 90, damping: 18 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-9, 9]), { stiffness: 90, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const ghostY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  const handleMouse = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    mouseX.set((event.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((event.clientY - rect.top) / rect.height - 0.5);
  };

  const lines = ["FASHION", "TECH", "OBJECTS"];
  return (
    <section ref={ref} onMouseMove={handleMouse} className="hero-spotlight relative flex min-h-screen items-center overflow-hidden px-5 pb-20 pt-32 lg:px-10" data-testid="kinetic-hero">
      <div className="hero-orb -left-24 top-10" />
      <div className="hero-orb -right-32 bottom-0" style={{ animationDelay: "-6s" }} />
      <motion.div style={{ y: ghostY }} className="pointer-events-none absolute -right-10 top-24 select-none font-heading text-[22vw] font-black italic leading-none text-outline opacity-20">
        BKG
      </motion.div>
      <div className="relative z-10 grid w-full items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-8 inline-flex items-center gap-3 border border-volt/40 bg-volt/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.24em] text-volt">
            <Sparkles size={14} /> BKG Global / 2026 Edit
          </motion.div>
          <h1 className="font-heading text-[clamp(4.6rem,13vw,13rem)] font-black italic leading-[0.78] tracking-[-0.08em]">
            {lines.map((line, index) => (
              <span key={line} className="masked-line">
                <motion.span
                  className={index === 1 ? "block text-volt" : "block"}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.28 + index * 0.12, ease: [0.76, 0, 0.24, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }} className="mt-8 max-w-xl text-base font-light leading-relaxed text-zinc-400 md:text-lg">
            BKG Global curates dark-luxury fashion, precision electronics, and future-facing objects for people who treat everyday life like an editorial.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05 }} className="mt-10 flex flex-wrap items-center gap-5">
            <button data-testid="hero-shop-button" onClick={onShop} className="kinetic-button border border-volt bg-volt px-8 py-4 text-sm font-extrabold uppercase tracking-[0.22em] text-black transition-colors hover:bg-transparent">
              Shop the edit
            </button>
            <a data-testid="hero-manifesto-link" href="#manifesto" className="group flex items-center gap-3 text-sm font-bold uppercase tracking-[0.22em] text-zinc-300 transition-colors hover:text-neon">
              Our manifesto <ArrowDownRight className="transition-transform group-hover:rotate-45" size={18} />
            </a>
          </motion.div>
        </div>
        <motion.div style={{ rotateX, rotateY, y: imageY }} initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.55 }} className="relative mx-auto h-[560px] w-full max-w-[520px] [perspective:1200px]">
          <div className="absolute right-0 top-0 h-[78%] w-[76%] overflow-hidden rounded-t-full border border-white/15 bg-zinc-900 shadow-[0_0_80px_rgba(204,255,0,0.16)]">
            <img src={heroImage} alt="BKG Global dark fashion editorial" className="h-full w-full object-cover" data-testid="hero-fashion-image" />
          </div>
          <div className="product-frame absolute bottom-0 left-0 h-[48%] w-[56%] overflow-hidden border border-white/15 bg-zinc-900">
            <img src={techImage} alt="BKG Global luxury electronics" className="h-full w-full object-cover" data-testid="hero-tech-image" />
          </div>
          <div className="glass-panel absolute bottom-16 right-2 max-w-[220px] p-5">
            <p className="text-xs uppercase tracking-[0.24em] text-volt">Curated drop</p>
            <p className="mt-2 font-heading text-3xl font-bold italic">Noir Circuit</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
